import { ActivatedRoute, Router } from '@angular/router';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { EstimatesService } from 'src/app/services/estimate-service/estimate.service';
import { PortalUrlContentDetailsModal } from '../../shared/modals/portal-url-content-details.modal';
import { PortalUrlService } from '../../services/portal-url.service/portal-url.service';
import { PortalUrlTypeEnum } from '../../shared/enums/portal-url/portal-url-type.enum';
import { ProjulRegex } from 'src/app/shared/utils/regex';
import { InvoiceService } from 'src/app/services/invoice-service/invoice.service';
import { InvoiceStatusEnum, PaymentProcessorOnboardingStatusTypes } from 'src/app/shared/enums/invoice/invoice.enum';
import { settingsProp } from 'src/app/shared/interfaces/settings/setting.interface';
import { SettingService } from 'src/app/services/setting-service/setting.service';
import { debounceTime, Subscription } from "rxjs";
import { InvoiceEntityDTO } from 'src/app/shared/invoice/invoice.interface';
import { validInvoicePaymentsExists } from 'src/app/shared/utils/util-function';
import { estimateTaxesPerLine, invoiceTaxesPerLine } from 'src/app/shared/utils/estimate-tax-mode';
import { MomentHelper } from 'src/app/shared/utils/moment-helper';
import { DataSyncService } from 'src/app/services/data-sync/data-sync.service';
import { GlobalStateService } from 'src/app/services/global-state.service';
import { CorrelationService } from 'src/app/services/correlation.service';
import { PreviewTokenService } from 'src/app/services/portal-url.service/preview-token.service';
import { PreviewLanguage, PreviewNotice, isLegacyPreviewLink, isPreviewRequest, previewLanguage, previewNoticeText } from 'src/app/services/portal-url.service/share-link-request';

@Component({
  selector: 'projul-portal-url',
  templateUrl: './portal-url.component.html',
  styleUrls: ['./portal-url.component.scss']
})
export class PortalUrlComponent implements OnInit, OnDestroy {
  isExists = false;
  isLoading = true;
  portalUrlTypeEnum = PortalUrlTypeEnum;
  invoiceStatusEnum = InvoiceStatusEnum
  content: PortalUrlContentDetailsModal = {} as PortalUrlContentDetailsModal;
  integrationSettings = { ...settingsProp };
  invoiceSettings = { ...settingsProp.InvoiceParentSettings };
  routeParamSub: Subscription;
  portalUrlId: any;
  previewNotice = '';
  private language: PreviewLanguage = 'en';

  constructor(private router: Router,
    private invoiceService: InvoiceService,
    private portalUrlService: PortalUrlService,
    private settingService: SettingService,
    private activatedRoute: ActivatedRoute,
    private estimateService: EstimatesService,
    private dataSyncService: DataSyncService,
    private globalStateService: GlobalStateService,
    private correlationService: CorrelationService,
    private previewTokens: PreviewTokenService
  ) {
  }


  ngOnInit() {
    this.routeParamSub = this.activatedRoute.params.subscribe(async p => {
      const id = p['id'];
      if (!id) {
        await this.router.navigate(['']);
        return
      }
      if (!id.match(ProjulRegex.guid)) {
        await this.router.navigate(['']);
        return
      }

      const fragment = this.activatedRoute.snapshot.fragment;
      this.language = previewLanguage(fragment, navigator.language);
      this.portalUrlId = id;
      this.correlationService.generate();

      // Without its token a preview must not load at all: loading it as the customer would record a customer view.
      if (isLegacyPreviewLink(this.activatedRoute.snapshot.queryParams)) {
        this.showPreviewNotice('unavailable');
        return;
      }
      const preview = isPreviewRequest(fragment);
      const previewToken = preview ? await this.previewTokens.request() : '';
      if (preview && !previewToken) {
        this.showPreviewNotice('unavailable');
        return;
      }

      this.globalStateService.getClientPortalRelatedDataChangesObservable.pipe(debounceTime(500)).subscribe(async () => {
        await this.FetchData(false, true);
      });

      await this.FetchData(true);

      // Live updates are for the customer's own view, not a signed-in user's preview.
      if (!previewToken && this.content?.Item)
        await this.dataSyncService.ConnectClient(this.content.Item.Id);
    });
  }

  async FetchData(sendNotification: boolean, silent: boolean = false) {
    // Silent refetch (SignalR-driven update): keep the previously rendered content in place
    // and just swap `this.content` once the new payload is ready — no spinner.
    if (!silent) this.isLoading = true;

    this.content = (await this.portalUrlService.Get(this.portalUrlId, sendNotification))?.Response as PortalUrlContentDetailsModal;
    this.previewNotice = this.content?.PreviewRefusal ? previewNoticeText(this.content.PreviewRefusal, this.language) : '';
    if (!this.content) {
      this.isExists = false;
      this.isLoading = false;
      return
    }
    if (!this.content.Item) {
      this.isExists = false;
      this.isLoading = false;
      return
    }

    if (this.content.Item?.IsPublished) {
      let taxes = this.content.Item.Taxes || [];
      let discounts = this.content.Item.Discounts || [];

      if (this.content.ItemType === PortalUrlTypeEnum.Invoice) {
        if (this.content.Item.EstimateId) {

          let otherInvoices = this.content.Item.OtherInvoices;

          this.content.Item.Taxes = null;
          this.content.Item.Discounts = null;
          this.content.Item.OtherInvoices = null;

          if (this.content.Item?.EstimateEntity) {
            let estimate = {
              ...this.content.Item.EstimateEntity,
              EstimateDetails: this.content.Item.EstimateEntity.EstimateDetails,
            };

            this.content.Item.EstimateEntity = estimate;
          }

          if (this.content.Item?.TaxId)
            this.content.Item.Tax = taxes?.find((f: any) => f.Id == this.content.Item?.TaxId);

          for (let estDetail of this.content.Item?.EstimateEntity?.EstimateDetails) {
            (estDetail.EstimateDiscounts || []).forEach((g: any) => {
              g.DiscountEntity = discounts?.find((t: any) => t.Id == g.DiscountId);
            });
          }

          const estimateTaxedPerLine = estimateTaxesPerLine(this.content.Item?.EstimateEntity);

          if (estimateTaxedPerLine) {
            for (let estDetail of this.content.Item?.EstimateEntity?.EstimateDetails) {
              (estDetail.EstimateDetailsSections || []).forEach((g: any) => {
                (g.EstimateCostLineItems || []).forEach((h: any) => {
                  if (h.TaxId) {
                    h.TaxEntity = taxes?.find((f: any) => f.Id == h.TaxId);
                  }
                });
                (g.SubEstimateDetails || []).forEach((j: any) => {
                  (j.EstimateCostLineItems || []).forEach((h: any) => {
                    if (h.TaxId) {
                      h.TaxEntity = taxes?.find((f: any) => f.Id == h.TaxId);
                    }
                  });
                });
              });
            }

          }
          let mapValues = await this.estimateService.mapEstimateSection(this.content.Item?.EstimateEntity, taxes, estimateTaxedPerLine);
          this.content.Item.EstimateEntity.EstimateDetails = mapValues.estimateDetails;

          let allInvoices = (otherInvoices).concat(this.content.Item);

          if (invoiceTaxesPerLine(this.content.Item)) {
            for (let inv of allInvoices) {
              (inv.AdditionalLineItems || []).forEach((g: any) => {
                g.Tax = taxes?.find((t: any) => t.Id == g.TaxId);
              });
            }
          }

          this.content.Item.EstimateEntity.EstimateValues = this.estimateService.getEstimateTotals(this.content.Item.EstimateEntity.EstimateDetails, taxes || [], allInvoices, estimateTaxedPerLine, this.content.Item.EstimateEntity.IsTemplate || false);
          this.content.Item.EstimateEntity.EstimateTotal = (this.content.Item.EstimateEntity.EstimateValues?.ApprovedProjectTotal || 0);
          this.content.Item.EstimateEntity.ProjectedProjectTotal = (this.content.Item.EstimateEntity.EstimateValues?.ProjectedProjectTotal || 0);

          for (let inv of allInvoices) {
            (inv.InvoiceDiscounts || []).forEach((g: any) => {
              g.DiscountEntity = discounts?.find((t: any) => t.Id == g.DiscountId);
            });

            if (inv.TaxId) {
              inv.Tax = taxes?.find((f: any) => f.Id == inv?.TaxId);
            }
          }

          allInvoices = await this.invoiceService.getFormattedInvoices(allInvoices, this.content.Item.EstimateEntity, estimateTaxedPerLine);
          this.estimateService.calculateInvoiceEstimateValues(this.content.Item.EstimateEntity, allInvoices);
          this.content.Item.Amount = this.invoiceService.getTotalLineItemsInvoicedPrice(this.content.Item.EstimatedLineItems);
          this.content.Item.OtherInvoices = [...allInvoices.filter((x: any) => x.Id !== this.content.Item.Id)];
        }
        else {
          (this.content.Item.InvoiceDiscounts || []).forEach((g: any) => {
            g.DiscountEntity = discounts?.find((t: any) => t.Id == g.DiscountId);
          });
          if (this.content.Item.TaxId) {
            this.content.Item.Tax = taxes?.find((f: any) => f.Id == this.content.Item?.TaxId);
          }
        }

        this.content.Item.EstimatedLineItems = await this.invoiceService.estimatedLineItemsMapping(this.content.Item.EstimateEntity, this.content.Item, estimateTaxesPerLine(this.content.Item.EstimateEntity));
        this.invoiceService.calculateInvoiceTotalValues(this.content.Item);

        let settingList = await this.settingService.getSettingList(this.content?.CompanyId);
        this.integrationSettings = this.settingService.mapSettingValues(settingList, this.integrationSettings);
        this.content.settings = this.settingService.mapSettingValues(settingList, this.invoiceSettings, this.invoiceSettings);
        let justifStatus = null;
        if (this.integrationSettings.JustiFiOnboardingSettings && (this.integrationSettings.JustiFiOnboardingSettings.Status || PaymentProcessorOnboardingStatusTypes.OnboardingNotStarted) == PaymentProcessorOnboardingStatusTypes.OnboardingCompleted) {
          this.integrationSettings.ViewJustiFiIntegrationForDemo = false;
          this.content.isJustiFiIntegrationCompleted = true;
          let data = this.integrationSettings.JustiFiOnboardingSettings as any;
          justifStatus = data?.Account?.status;
          if (!this.integrationSettings.PaymentsEnabledForPastInvoices) {
            if (data && data.Status != PaymentProcessorOnboardingStatusTypes.OnboardingNotStarted) {
              let date1 = new Date(this.content?.Item?.CreatedDate);
              let date2 = new Date(data?.Business?.data?.created_at);
              if (date1 >= date2) { // date1 === date2 //
                this.integrationSettings.PaymentsEnabledForPastInvoices = true;
              }
              else if (this.content.Item.EnablePayment) {
                this.integrationSettings.PaymentsEnabledForPastInvoices = true;
              }
            }
            else if (this.integrationSettings.ViewJustiFiIntegrationForDemo) {
              this.SettingDemoAccountSettings();
            }
          }
          else if (!data || ((data && data?.Status == PaymentProcessorOnboardingStatusTypes.OnboardingNotStarted))) {
            if (this.integrationSettings.ViewJustiFiIntegrationForDemo) {
              this.SettingDemoAccountSettings();
            }
            else
              this.integrationSettings.PaymentsEnabledForPastInvoices = false;
          }
          else if ((data && data?.Status != PaymentProcessorOnboardingStatusTypes.OnboardingNotStarted)) {
            this.integrationSettings.ViewJustiFiIntegrationForDemo = false;
          }
        }
        else if (this.integrationSettings.ViewJustiFiIntegrationForDemo) {
          this.integrationSettings.PaymentsEnabledForPastInvoices = true;
        }
        if (this.integrationSettings?.PaymentProcessorSettings?.IsJustiFiEnabled == false || justifStatus == "disabled") {
          this.integrationSettings.PaymentsEnabledForPastInvoices = false;
        }
        this.content.Item.Status = this.invoiceService.getInvoiceStatus(this.content.Item);
      }
    }

    this.isLoading = false;
    this.isExists = !!this.content;
  }

  private showPreviewNotice(notice: PreviewNotice) {
    this.previewNotice = previewNoticeText(notice, this.language);
    this.isExists = false;
    this.isLoading = false;
  }

  SettingDemoAccountSettings() {
    this.integrationSettings.PaymentsEnabledForPastInvoices = true;
  }

  ngOnDestroy(): void {
    this.routeParamSub?.unsubscribe();
    this.correlationService.clear();
  }

  getInvoiceStatus(invoice: InvoiceEntityDTO) {
    let statusToUpdate;
    if (invoice.ForceComplete) {
      return InvoiceStatusEnum.Paid;
    }

    if (validInvoicePaymentsExists(invoice.Payments)) {
      if (invoice.RemainingBalance > 0) {
        if (invoice.InvoiceDueDate && MomentHelper.IsBefore(invoice.InvoiceDueDate, MomentHelper.GetDate())) {
          statusToUpdate = InvoiceStatusEnum.Overdue;
        } else {
          statusToUpdate = InvoiceStatusEnum.Partial;
        }
      } else {
        statusToUpdate = InvoiceStatusEnum.Paid;
      }
    }
    else {
      if (invoice.Status == InvoiceStatusEnum.Paid)//If invoice has paid status in db and there is no payment against it then we will set its status to Paid.
      {
        return InvoiceStatusEnum.Paid;
      }
      else {
        if (invoice.InvoiceDueDate && MomentHelper.IsBefore(invoice.InvoiceDueDate, MomentHelper.GetDate())) {
          statusToUpdate = InvoiceStatusEnum.Overdue;
        } else {
          statusToUpdate = invoice.Status;
        }
      }

    }
    return statusToUpdate;
  }

}


